/**
 * useUnifiedProjects Hook
 *
 * React hook for interacting with the Unified Projects API.
 * Provides CRUD operations for projects and app management.
 *
 * @param {string|null} appFilter - Filter projects by app ('site_dna', 'flux_ev', 'flux_ems') or null for all
 * @returns {Object} Hook interface with state and methods
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/useAuth';
import { API_BASE_URL } from '../services/api';

export function useUnifiedProjects(appFilter = null) {
    const { currentUser } = useAuth();

    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Load projects from API
     * Auto-called on mount and when appFilter changes
     */
    const loadProjects = useCallback(async () => {
        if (!currentUser) {
            setProjects([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const token = await currentUser.getIdToken();
            const url = appFilter
                ? `${API_BASE_URL}/api/projects/v1/projects?app=${appFilter}`
                : `${API_BASE_URL}/api/projects/v1/projects`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || `Failed to load projects: ${response.status}`);
            }

            const data = await response.json();
            setProjects(data.projects || []);
        } catch (err) {
            console.error('Error loading projects:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser, appFilter]);

    /**
     * Create a new project
     * @param {Object} projectData - Project data (name, description, apps)
     * @returns {Promise<Object>} Created project
     * @throws {Error} If creation fails
     */
    const createProject = useCallback(async (projectData) => {
        if (!currentUser) {
            throw new Error('Authentication required');
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/projects/v1/projects`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(projectData),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || `Failed to create project: ${response.status}`);
        }

        const data = await response.json();
        const newProject = data.project || data;

        // Optimistically update local state
        setProjects(prev => [newProject, ...prev]);

        return newProject;
    }, [currentUser]);

    /**
     * Update an existing project
     * @param {string} projectId - Project ID
     * @param {Object} updates - Fields to update
     * @returns {Promise<Object>} Updated project
     * @throws {Error} If update fails
     */
    const updateProject = useCallback(async (projectId, updates) => {
        if (!currentUser) {
            throw new Error('Authentication required');
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/projects/v1/projects/${projectId}`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(updates),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || `Failed to update project: ${response.status}`);
        }

        const data = await response.json();
        const updatedProject = data.project || data;

        // Optimistically update local state
        setProjects(prev =>
            prev.map(p => p.id === projectId ? updatedProject : p)
        );

        return updatedProject;
    }, [currentUser]);

    /**
     * Delete (soft delete) a project
     * @param {string} projectId - Project ID
     * @returns {Promise<void>}
     * @throws {Error} If deletion fails
     */
    const deleteProject = useCallback(async (projectId) => {
        if (!currentUser) {
            throw new Error('Authentication required');
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/projects/v1/projects/${projectId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || `Failed to delete project: ${response.status}`);
        }

        // Optimistically update local state
        setProjects(prev => prev.filter(p => p.id !== projectId));
    }, [currentUser]);

    /**
     * Enable an app for a project
     * @param {string} projectId - Project ID
     * @param {string} appName - App name ('site_dna', 'flux_ev', 'flux_ems')
     * @param {Object} config - App-specific configuration
     * @returns {Promise<Object>} Updated project
     * @throws {Error} If operation fails
     */
    const enableApp = useCallback(async (projectId, appName, config = {}) => {
        if (!currentUser) {
            throw new Error('Authentication required');
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/projects/v1/projects/${projectId}/apps/${appName}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ config }),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || `Failed to enable app: ${response.status}`);
        }

        const data = await response.json();
        const updatedProject = data.project || data;

        // Optimistically update local state
        setProjects(prev =>
            prev.map(p => p.id === projectId ? updatedProject : p)
        );

        return updatedProject;
    }, [currentUser]);

    /**
     * Duplicate a project (shell only — metadata, settings, apps; no scenarios)
     * @param {string} projectId - Project ID to duplicate
     * @param {string} [name] - Optional custom name for the copy
     * @returns {Promise<Object>} Duplicated project
     * @throws {Error} If duplication fails
     */
    const duplicateProject = useCallback(async (projectId, name) => {
        if (!currentUser) {
            throw new Error('Authentication required');
        }

        const token = await currentUser.getIdToken();
        const body = name ? { name } : {};
        const response = await fetch(`${API_BASE_URL}/api/projects/v1/projects/${projectId}/duplicate`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || `Failed to duplicate project: ${response.status}`);
        }

        const data = await response.json();
        const newProject = data.project || data;

        // Optimistically add to local state
        setProjects(prev => [newProject, ...prev]);

        return newProject;
    }, [currentUser]);

    /**
     * Disable an app for a project
     * @param {string} projectId - Project ID
     * @param {string} appName - App name ('site_dna', 'flux_ev', 'flux_ems')
     * @returns {Promise<Object>} Updated project
     * @throws {Error} If operation fails
     */
    const disableApp = useCallback(async (projectId, appName) => {
        if (!currentUser) {
            throw new Error('Authentication required');
        }

        const token = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/projects/v1/projects/${projectId}/apps/${appName}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || `Failed to disable app: ${response.status}`);
        }

        const data = await response.json();
        const updatedProject = data.project || data;

        // Optimistically update local state
        setProjects(prev =>
            prev.map(p => p.id === projectId ? updatedProject : p)
        );

        return updatedProject;
    }, [currentUser]);

    // Auto-load projects on mount and when dependencies change
    useEffect(() => {
        loadProjects();
    }, [loadProjects]);

    return {
        projects,
        loading,
        error,
        loadProjects,
        createProject,
        updateProject,
        deleteProject,
        duplicateProject,
        enableApp,
        disableApp,
    };
}
